import { Checkbox } from '@calefy-inc/informedMaterial';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import {
  ArrayField,
  useFormApi,
  useArrayFieldItemState,
  useArrayFieldItemApi,
} from 'informed';
import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import RenderedQuestionInstance from './RenderedQuestionInstance';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeMotionRoundedIcon from '@mui/icons-material/AutoAwesomeMotionRounded';
// import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AddItemButton } from './common';
import { Component } from './classes';
import { clearArrayFieldToCopy } from '../../../store/QuoteWizardState';
import { StoreState } from '../../../store';
import { copyArrayFormValues } from '../../../util/formCopyUtils';

import {
  QuoteComponentProps,
  ProgramBuilderComponentProps,
  QuoteComponentValidationFunctionReturn,
} from './types';
import { ProgramBuilderQuestionInstance } from '../../FormManager/classes';
import { QuoteWizardQuestionInstance } from '../../QuoteWizard/classes';
import { HelpIcon } from '../HelpIcon';
import type { $TSFixMe } from '@calefy-inc/utilityTypes';

const ProgramBuilderComponent = ({
  classes = {},
  questionInstance,
}: ProgramBuilderComponentProps) => {
  const propsBlob =
    questionInstance && questionInstance.propsBlob
      ? questionInstance.propsBlob
      : {};
  const repeatable = propsBlob.repeatable ? true : false;

  return (
    <>
      <Checkbox
        classes={classes}
        className={classes.checkbox}
        name='props_blob_repeatable'
        label='Repeatable set'
        value={repeatable}
      />
    </>
  );
};

// Separate component to use hooks inside ArrayField.Items
interface RepeatableItemProps {
  questionInstance: QuoteWizardQuestionInstance;
  totalFields: number;
  classes: $TSFixMe;
  keepStateIfRelevant?: boolean;
}

const RepeatableItem = ({
  questionInstance,
  totalFields,
  classes,
  keepStateIfRelevant = false,
}: RepeatableItemProps) => {
  // Use the hook to access array field item state
  const { name, index } = useArrayFieldItemState();
  const { remove } = useArrayFieldItemApi();

  return (
    <>
      {totalFields > 1 && index !== 0 ? (
        <Divider className={classes.divider} />
      ) : null}
      <div className={classes.addressHeader}>
        {totalFields > 1 ? (
          <Typography
            variant='subtitle1'
            color='primary'
            className={classes.start}
          >
            {`${questionInstance.displayName} ${index + 1}`}
          </Typography>
        ) : null}
        <div className={classes.removeAddress}>
          {totalFields > 1 ? (
            <IconButton
              color='default'
              size='small'
              onClick={() => {
                remove();
              }}
              type='button'
            >
              <Tooltip
                enterDelay={100}
                leaveDelay={100}
                placement='right'
                title={`Remove ${questionInstance.displayName}`}
              >
                <CloseIcon />
              </Tooltip>
            </IconButton>
          ) : undefined}
        </div>
      </div>
      {questionInstance.subQuestions.map((subQ) => {
        // Set field prefix using 'name' from the hook instead of from render args
        subQ.fieldPrefix = name;
        return (
          <RenderedQuestionInstance
            questionInstance={subQ}
            key={subQ.id}
            classes={classes}
            keepStateIfRelevant={keepStateIfRelevant}
          />
        );
      })}
    </>
  );
};

const QuoteComponent = function ({
  questionInstance,
  blockAddSubquestions,
  classes = {},
  keepStateIfRelevant = false,
}: QuoteComponentProps) {
  const dispatch = useDispatch();
  const formApi = useFormApi();

  // Create memoized selector function that generates field name and filters copy requests
  const selectMatchingCopyRequests = useCallback(
    (state: StoreState) => {
      const currentFieldName = questionInstance.generateFieldName();
      const leafApiName = questionInstance.apiName;

      return state.quoteWizard.arrayFieldsToCopy.filter(
        (request) =>
          request.leafApiName === leafApiName &&
          request.fullQuestionFieldName === currentFieldName,
      );
    },
    [questionInstance],
  );

  const matchingCopyRequests = useSelector(selectMatchingCopyRequests);
  //Render the top level question, then call RenderQuestionInstance on all the other questions so they look nice
  // Define a callback function that fires on clicking the "Add new" button
  // The "Add new" button should add a new set of fieldsets to the form's state using the Informed field API

  /* {
    form : {
      fieldset: [
        [fieldset1:
          q1: a1
          q2: a2],
        [fieldset2: //so this is what we'd add
          q1: a1:
          q2: a2]
      ]

    }
  }*/
  const { propsBlob } = questionInstance;
  const { repeatable } = propsBlob;

  /*console.log('SET OF QUESTIONS: about to render ', questionInstance); */

  return (
    <>
      <Grid container alignItems='flex-end'>
        <Grid item xs={questionInstance.helpText ? 11 : 12}>
          <Typography
            variant='subtitle1'
            color='textSecondary'
            className={classes.center}
          >
            {questionInstance.getQuoteQuestionLabel()}
          </Typography>
        </Grid>
        {questionInstance.helpText ? (
          <Grid
            item
            xs={1}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <HelpIcon title={questionInstance.helpText} />
          </Grid>
        ) : null}
      </Grid>
      <ArrayField name={questionInstance.apiName}>
        {({ fields, add }) => {
          // Handle copy requests within ArrayField context
          if (matchingCopyRequests.length > 0) {
            matchingCopyRequests.forEach((request) => {
              // Read the source array using the full source field name
              const sourceArray = formApi.getValue(request.fullSourceFieldName);

              if (Array.isArray(sourceArray)) {
                // Use arrayFieldApi to add the appropriate number of entries
                const currentLength = fields.length;
                const targetLength = sourceArray.length;

                // Add the required number of array items first
                Array.from({ length: targetLength - currentLength }).forEach(
                  () => {
                    add();
                  },
                );

                // Now copy the values from the source array to the destination array
                // We need to do this after adding items to ensure the form state is updated
                setTimeout(() => {
                  try {
                    const { success, errors } = copyArrayFormValues(
                      formApi,
                      sourceArray,
                      request.fullSourceFieldName,
                      request.fullQuestionFieldName,
                    );

                    if (!success) {
                      console.error(
                        'Array copying failed with errors:',
                        errors,
                      );
                      // Optionally dispatch an error action or show user feedback
                    }
                  } catch (error) {
                    console.error(
                      'Unexpected error during array copying:',
                      error,
                    );
                  }
                }, 100); // Small delay to ensure form state is updated after adding items
              }

              // Clear this specific copy request from state
              dispatch(
                clearArrayFieldToCopy({
                  leafApiName: request.leafApiName,
                  fullQuestionFieldName: request.fullQuestionFieldName,
                  fullSourceFieldName: request.fullSourceFieldName,
                }),
              );
            });
          } else {
            // This is here because otherwise we add one too many. Basically: if we're copying just do that, otherwise add the blank one.
            // debugger;
            if (fields.length === 0) {
              // debugger;
              add();
              // arrayFieldApi.add();
            }
          }

          return (
            <>
              <ArrayField.Items>
                {() => (
                  <RepeatableItem
                    questionInstance={questionInstance}
                    totalFields={fields.length}
                    classes={classes}
                    keepStateIfRelevant={keepStateIfRelevant}
                  />
                )}
              </ArrayField.Items>
              {repeatable && !blockAddSubquestions ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AddItemButton
                    text={`Add ${questionInstance.displayName}`}
                    aria-label='Add'
                    className={classes.button}
                    type='button'
                    color='primary'
                    onClick={() => {
                      add();
                    }}
                    size='large'
                  />
                </div>
              ) : null}
            </>
          );
        }}
      </ArrayField>
    </>
  );
};

//The point of this component is to gather all the subQuestions and add them
//to the questionInstance, then return that instance. The remaining steps should
//happen in formStore
/* const gatherSubQuestions = ( */
/*   formState: $TSFixMe, */
/*   questionInstance: $TSFixMe, */
/* ) => { */
/*   //look at form state, pick out the first question */
/*   //push it into the first slot in the subQuestions array */
/*   let temp_arr = formState.subQuestions; */
/*   questionInstance.subQuestions = temp_arr; */
/*   return questionInstance; */
/* }; */

export function validateRepeatable(
  questionInstance: ProgramBuilderQuestionInstance,
): QuoteComponentValidationFunctionReturn {
  const { subQuestions } = questionInstance;
  const errorMessages = [];
  if (!subQuestions || subQuestions.length === 0) {
    errorMessages.push(
      `Set of Questions with name "${questionInstance.generateDisplayName()}" missing a subquestion; there must be at least one.`,
    );
  }
  return errorMessages;
}

const IconComponent = () => {
  return <AutoAwesomeMotionRoundedIcon fontSize='small' color='primary' />;
};

export default function createComponentRegistry() {
  return new Component({
    type: 'repeatable',
    typeLabel: 'Set of Questions',
    /* gatherSubQuestions: gatherSubQuestions, */
    quoteComponent: QuoteComponent,
    dataType: 'nested',
    programBuilderComponent: ProgramBuilderComponent,
    programBuilderValidation: validateRepeatable,
    icon: IconComponent,
  });
}

export { QuoteComponent };
